import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type AiQuota = {
  lookupLimit: number;
  lookupRemaining: number;
  analyzeLimit: number;
  analyzeRemaining: number;
  isUnlimited?: boolean;
};

export function aiQuotaMonthKey() {
  const d = new Date();
  const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0));
  return m.toISOString().slice(0, 10);
}

export function useAiQuota(userId?: string) {
  const month = aiQuotaMonthKey();
  return useQuery({
    queryKey: ["ai-quota", userId, month],
    enabled: !!userId,
    queryFn: async (): Promise<AiQuota> => {
      const [{ data: isAdmin }, { data: isPremium }] = await Promise.all([
        supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
        supabase.rpc("has_role", { _user_id: userId, _role: "premium" }),
      ]);
      if (isAdmin) {
        return {
          lookupLimit: Number.POSITIVE_INFINITY,
          lookupRemaining: Number.POSITIVE_INFINITY,
          analyzeLimit: Number.POSITIVE_INFINITY,
          analyzeRemaining: Number.POSITIVE_INFINITY,
          isUnlimited: true,
        };
      }
      const premium = !!isPremium;

      const { data, error } = await supabase
        .from("ai_quota_monthly")
        .select("lookup_limit,lookup_remaining,analyze_limit,analyze_remaining")
        .eq("user_id", userId)
        .eq("month", month)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          lookupLimit: premium ? 300 : 50,
          lookupRemaining: premium ? 300 : 50,
          analyzeLimit: premium ? 150 : 20,
          analyzeRemaining: premium ? 150 : 20,
        };
      }

      return {
        lookupLimit: Number(data.lookup_limit) || (premium ? 300 : 50),
        lookupRemaining: Number(data.lookup_remaining) || 0,
        analyzeLimit: Number(data.analyze_limit) || (premium ? 150 : 20),
        analyzeRemaining: Number(data.analyze_remaining) || 0,
        isUnlimited: false,
      };
    },
  });
}

